import { QuotePricingSnapshot } from './quotePricing';
import { AdhocPackage, QuoteSession } from './quoteSessions';

export function clientFacingShootType(value?: string) {
  const text = String(value || '').trim();
  if (!text || /^(any|no preference|no-preference|none|n\/a)$/i.test(text)) return '';
  return text;
}

export interface QuoteWizardDraft {
  packageId: string;
  currency: 'R' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  discountPercent: number;
  depositPercent: number;
  overridePackagePrice: number | null;
  overridePackageCurrency: string | null;
  selectedAddOns: Array<Record<string, unknown>>;
  customAddons: Array<Record<string, unknown>>;
  clientName: string;
  personalMessage: string;
  recipientEmail: string;
  clientPhone?: string;
  countryCode?: string;
  contactPreference?: '' | 'email' | 'whatsapp' | 'either';
  companyName?: string;
  /** Postal address for invoicing. Empty when the client does not need one. */
  billingAddress?: string;
  shootType: string;
  preferredDate?: string;
  preferredTime?: string;
  preferredLocation?: { name?: string; address?: string; mapsUrl?: string };
  /** Multi-day schedule; first session mirrors preferredDate/time/location. */
  sessions?: QuoteSession[];
  /** One-off package that lives only on this quotation. */
  adhocPackage?: AdhocPackage | null;
}

export interface StoredQuote {
  id: string;
  quoteNumber: string;
  version: number;
  parentId?: string | null;
  status: 'draft' | 'issued' | 'accepted' | 'declined' | 'expired' | 'cancelled' | 'superseded';
  createdAt: string;
  updatedAt: string;
  issuedAt?: string | null;
  origin?: '' | 'recorded';
  externalReference?: string;
  acceptedTerms?: string | null;
  /** `email` when the studio recorded an acceptance that arrived by email. */
  acceptanceChannel?: '' | 'email';
  client?: {
    name?: string;
    email?: string;
    message?: string;
    company?: string;
    /** Postal address for invoicing. Empty when the client does not need one. */
    billingAddress?: string;
    countryCode?: string;
    phoneE164?: string;
    preference?: '' | 'email' | 'whatsapp' | 'either';
    contactCreatedAt?: string;
    contactUpdatedAt?: string;
  };
  shootType?: string;
  /** YYYY-MM-DD the client asked for, or '' when the date is to be decided. */
  preferredDate?: string;
  /** HH:MM start time the client asked for, or '' for no preference. */
  preferredTime?: string;
  /** Where the client would like the shoot, or empty fields when it is to be decided. */
  preferredLocation?: { name?: string; address?: string; mapsUrl?: string };
  /**
   * Dated coverage blocks for multi-day work.
   * When present, preferredDate / preferredTime / preferredLocation mirror the first session.
   */
  sessions?: QuoteSession[];
  /** One-off package belonging only to this quotation (never saved to the catalogue). */
  adhocPackage?: AdhocPackage | null;
  /** Guide library ids included with this quotation. */
  guideIds?: string[];
  snapshot: QuotePricingSnapshot;
  draft?: QuoteWizardDraft | null;
  lockedZarMinor?: number | null;
  clientToken?: string | null;
  linkRevokedAt?: string | null;
  zoho?: null;
  payfast?: null;
  booking?: null;
  archivedAt?: string | null;
  archivedBy?: string;
  deletedAt?: string | null;
  deletedBy?: string;
  deleteReason?: string;
  isTest?: boolean;
  activity?: Array<{
    at: string;
    action: string;
    by?: string;
    changes?: string[];
    fields?: string[];
    from?: string | { name?: string; address?: string; mapsUrl?: string } | null;
    to?: string | { name?: string; address?: string; mapsUrl?: string } | null;
    reason?: string;
    detail?: string;
  }>;
}
